import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './ProposalComparisonView.module.css';
import { proposalComparisonService, homeownerBillingService } from '../../../services/api';
import { formatDate } from '../../../utils/formatters';
import Toast from '../../../components/common/Toast/Toast';
import ComparisonOverview from './ComparisonOverview';
import DetailedAnalysis from './DetailedAnalysis';
import ContractorProfiles from './ContractorProfiles';
import ExpertQuestions from './ExpertQuestions';

const ProposalComparisonView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [userPlan, setUserPlan] = useState('free');
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Data states
  const [recommendationData, setRecommendationData] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [clarificationData, setClarificationData] = useState(null);
  const [guestAnalysisData, setGuestAnalysisData] = useState(null);
  const [savedComparisonData, setSavedComparisonData] = useState(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Comparison management states
  const [comparisonId, setComparisonId] = useState(id);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // NEW: Premium processing status states
  const [isPremiumProcessing, setIsPremiumProcessing] = useState(false);
  const [premiumProcessingStatus, setPremiumProcessingStatus] = useState(null);
  const [showProcessingMessage, setShowProcessingMessage] = useState(false);
  
  // Load comparison data from the new JSON files
  useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (id) {
          // First, get the basic comparison data
          const response = await proposalComparisonService.getComparisonById(id, false);
          
          if (response.data && response.data.status === 'success') {
            setSavedComparisonData(response.data.data);
            setComparisonId(response.data.data.id);
            
            // Now load the JSON files from the project directory
            const userId = response.data.data.user_id;
            const projectName = response.data.data.project_name;
            
            if (userId && projectName) {
              await loadAnalysisFiles(userId, projectName);
            } else {
              throw new Error('Missing user ID or project name');
            }
          } else {
            throw new Error('Invalid response format from server');
          }
        }
      } catch (err) {
        console.error('Error loading comparison data:', err);
        setError("Failed to load comparison data. Please try again.");
        setToast({
          type: 'error',
          message: 'Error loading comparison data'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparisonData();
  }, [id]);

  // Load analysis files from the backend using single API call
  const loadAnalysisFiles = async (userId, projectName) => {
    try {
      const response = await proposalComparisonService.getProjectAnalysisData(
        userId, 
        projectName
      );
      
      if (response.data && response.data.status === 'success') {
        const analysisData = response.data.data;
        
        // Set the individual data states based on what was returned
        if (analysisData.recommendation) {
          setRecommendationData(analysisData.recommendation);
        }
        
        if (analysisData.evaluation) {
          setEvaluationData(analysisData.evaluation);
        }
        
        if (analysisData.consolidated_proposal_analysis) {
          setConsolidatedData(analysisData.consolidated_proposal_analysis);
        }
        
        if (analysisData.clarification) {
          setClarificationData(analysisData.clarification);
        }
        
        // For free tier users, extract guest analysis data
        if (analysisData.guestAnalysis) {
          setGuestAnalysisData(analysisData.guestAnalysis);
        }
        
        console.log(`Analysis data loaded: ${response.data.filesFound}/${response.data.totalFiles} files found`);
        console.log('Is free tier data:', response.data.isFreeTierData);
        console.log('Guest analysis data:', analysisData.guestAnalysis);
      }
    } catch (error) {
      console.error('Error loading analysis files:', error);
      // Don't show error toast for analysis file loading failures
      // as the overview can still work with partial data
    }
  };

  // Check if user has premium access
  const isPremiumUnlocked = userPlan !== 'free';

  // NEW: Check premium analysis status for premium users
  useEffect(() => {
    const checkPremiumAnalysisStatus = async () => {
      // Only check for authenticated users (not guests)
      if (!comparisonId || !isPremiumUnlocked) return;
      
      try {
        const response = await proposalComparisonService.checkPremiumAnalysisStatus(comparisonId);
        
        if (response.data && response.data.status === 'success') {
          const statusData = response.data.data;
          setPremiumProcessingStatus(statusData);
          
          console.log('Premium analysis status:', statusData);
          
          // If user is premium and there are active processes, show processing message
          if (statusData.isPremium && statusData.activeProcesses > 0 && !statusData.processingFailed) {
            setShowProcessingMessage(true);
            setIsPremiumProcessing(true);
          } else {
            setShowProcessingMessage(false);
            setIsPremiumProcessing(false);
          }
          
          // If processing failed, show error immediately
          if (statusData.processingFailed) {
            const errorMessage = statusData.errorDetails && statusData.errorDetails.length > 0
              ? `Analysis failed: ${statusData.errorDetails[0].content}`
              : 'Analysis failed due to processing errors. Please try again or contact support.';
              
            setError(errorMessage);
            setToast({
              type: 'error',
              message: errorMessage,
              duration: 8000
            });
          }
        }
      } catch (error) {
        console.error('Error checking premium analysis status:', error);
        // Clear processing states and show error to user
        setShowProcessingMessage(false);
        setIsPremiumProcessing(false);
        setToast({
          type: 'error',
          message: 'Unable to check analysis status. Please refresh the page or contact support.',
          duration: 6000
        });
      }
    };

    checkPremiumAnalysisStatus();
  }, [comparisonId, isPremiumUnlocked]);

  // NEW: Poll premium analysis status if processing is in progress
  useEffect(() => {
    if (!isPremiumProcessing) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await proposalComparisonService.checkPremiumAnalysisStatus(comparisonId);
        
        if (response.data && response.data.status === 'success') {
          const statusData = response.data.data;
          setPremiumProcessingStatus(statusData);
          
          // If processing is complete (no active processes and processing is complete), stop polling and hide message
          if (statusData.activeProcesses === 0 && statusData.processingComplete) {
            setIsPremiumProcessing(false);
            setShowProcessingMessage(false);
            
            // Show success toast
            setToast({
              type: 'success',
              message: '🎉 Premium analysis complete! You will receive an email notification with your detailed analysis.',
              duration: 5000
            });
            
            // Reload the page to fetch the updated analysis data
            window.location.reload();
          }
          
          // If processing failed, stop polling and hide message with error
          if (statusData.processingFailed) {
            setIsPremiumProcessing(false);
            setShowProcessingMessage(false);
            
            // Show error toast with details
            const errorMessage = statusData.errorDetails && statusData.errorDetails.length > 0
              ? `Analysis failed: ${statusData.errorDetails[0].content}`
              : 'Analysis failed due to processing errors. Please try again or contact support.';
              
            setToast({
              type: 'error',
              message: errorMessage,
              duration: 8000
            });
            
            // Set error state so component shows appropriate UI
            setError(errorMessage);
          }
        }
              } catch (error) {
          console.error('Error polling premium analysis status:', error);
          // If there's a persistent polling error, stop polling after some attempts
          // and clear the processing overlay to prevent infinite loading
          setIsPremiumProcessing(false);
          setShowProcessingMessage(false);
          setToast({
            type: 'error',
            message: 'Unable to check analysis status. Please refresh the page or contact support.',
            duration: 6000
          });
        }
    }, 30000); // Poll every 30 seconds

    // Cleanup interval on unmount or when processing completes
    return () => clearInterval(pollInterval);
  }, [isPremiumProcessing, comparisonId]);

  // Fetch user subscription status
  const fetchUserSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const response = await homeownerBillingService.getSubscription();
      
      if (response.data.status === 'success') {
        const subscriptionData = response.data.data;
        
        if (subscriptionData && subscriptionData.status === 'active') {
          // Map plan name to lowercase format
          const planName = subscriptionData.plan_name.toLowerCase();
          setUserPlan(planName);
          console.log('User subscription found:', planName);
        } else {
          // No active subscription found, user is on free plan
          setUserPlan('free');
          console.log('No active subscription, user is on free plan');
        }
      } else {
        // API error, default to free plan
        setUserPlan('free');
        console.log('API error, defaulting to free plan');
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      // On error, default to free plan
      setUserPlan('free');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Load subscription status
  useEffect(() => {
    fetchUserSubscription();
  }, []);

  const handleUpgradeClick = () => {
    navigate('/homeowner/pricing');
  };

  // Handle comparison deletion
  const handleDeleteComparison = async () => {
    if (!comparisonId) {
      setToast({
        type: 'error',
        message: 'No comparison ID available to delete'
      });
      return;
    }
    
    try {
      setIsDeleting(true);
      
      const response = await proposalComparisonService.deleteComparison(comparisonId);
      
      if (response.data && response.data.status === 'success') {
        setToast({
          type: 'success',
          message: 'Comparison deleted successfully'
        });
        
        setTimeout(() => {
          navigate('/homeowner/dashboard');
        }, 1500);
      } else {
        throw new Error('Failed to delete comparison');
      }
    } catch (error) {
      console.error('Error deleting comparison:', error);
      setToast({
        type: 'error',
        message: `Delete failed: ${error.message || 'Unknown error'}`
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };



  // Render delete confirmation modal
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.deleteModal}>
          <div className={styles.deleteModalHeader}>
            <h2>Delete Comparison</h2>
            <button className={styles.closeModal} onClick={() => setShowDeleteModal(false)}>×</button>
          </div>
          
          <div className={styles.deleteModalContent}>
            <div className={styles.deleteWarningIcon}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <p>Are you sure you want to delete this comparison?</p>
            <p className={styles.deleteWarning}>This action cannot be undone. All associated files will also be deleted.</p>
          </div>
          
          <div className={styles.deleteModalFooter}>
            <button 
              className={styles.cancelButton} 
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              className={styles.deleteButton} 
              onClick={handleDeleteComparison}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className={styles.buttonSpinner}></div>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt"></i> Delete Permanently
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading || subscriptionLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading comparison analysis...</p>
      </div>
    );
  }

  // Don't do early return for errors - show them within the normal page structure

  return (
    <div className={styles.comparisonViewContainer}>
      {renderDeleteModal()}
      {toast && <Toast 
        type={toast.type} 
        message={toast.message} 
        onClose={() => setToast(null)}
      />}
      
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/homeowner/dashboard')}>
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
        <div className={styles.headerTitleSection}>
          <h1>
            Proposal Comparison
            {savedComparisonData && savedComparisonData.project_name && (
              <span className={styles.projectNameInline}>: {savedComparisonData.project_name}</span>
            )}
          </h1>
          {savedComparisonData && (
            <div className={styles.projectDate}>
              <i className="fas fa-calendar"></i>
              Created {formatDate(savedComparisonData.created_at)}
            </div>
          )}
        </div>
        <div className={styles.headerActions}>
          {/* {isPremiumUnlocked ? (
            <div className={styles.premiumBadge}>
              <i className="fas fa-crown"></i> {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} Plan
            </div>
          ) : (
            <button 
              className={styles.unlockPremiumButton} 
              onClick={handleUpgradeClick}
              title="Unlock Premium Features"
            >
              <i className="fas fa-crown"></i> Unlock Premium
            </button>
          )} */}
          <button 
            className={styles.deleteComparisonButton} 
            onClick={() => setShowDeleteModal(true)}
            title="Delete Comparison"
          >
            <i className="fas fa-trash-alt"></i> Delete
          </button>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-eye"></i> Overview
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'detailed' ? styles.active : ''} ${!isPremiumUnlocked ? styles.locked : ''}`}
            onClick={() => isPremiumUnlocked ? setActiveTab('detailed') : handleUpgradeClick()}
          >
            <i className="fas fa-chart-line"></i> Detailed Analysis
            {!isPremiumUnlocked && <i className="fas fa-lock"></i>}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'contractors' ? styles.active : ''} ${(!isPremiumUnlocked || !['complete', 'expert'].includes(userPlan)) ? styles.locked : ''}`}
            onClick={() => (isPremiumUnlocked && ['complete', 'expert'].includes(userPlan)) ? setActiveTab('contractors') : handleUpgradeClick()}
          >
            <i className="fas fa-hard-hat"></i> Contractors
            {(!isPremiumUnlocked || !['complete', 'expert'].includes(userPlan)) && <i className="fas fa-lock"></i>}
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'questions' ? styles.active : ''} ${(!isPremiumUnlocked || !['complete', 'expert'].includes(userPlan)) ? styles.locked : ''}`}
            onClick={() => (isPremiumUnlocked && ['complete', 'expert'].includes(userPlan)) ? setActiveTab('questions') : handleUpgradeClick()}
          >
            <i className="fas fa-question-circle"></i> Questions
            {(!isPremiumUnlocked || !['complete', 'expert'].includes(userPlan)) && <i className="fas fa-lock"></i>}
          </button>
        </div>
      </div>

      {/* NEW: Premium Processing Message */}
      {showProcessingMessage && (
        <div className={styles.processingOverlay}>
          <div className={styles.processingCard}>
            <div className={styles.processingHeader}>
              <div className={styles.processingSpinner}>
                <div className={styles.spinner}></div>
              </div>
              <h3>🤖 AI Analysis in Progress</h3>
            </div>
            <div className={styles.processingContent}>
              <p>
                <strong>Your premium analysis is currently being processed!</strong>
              </p>
              <p>
                Our AI is working hard to generate detailed clarification questions and advanced insights for your proposal comparison.
              </p>
              
              <div className={styles.processingDetails}>
                {premiumProcessingStatus?.currentProcess ? (
                  <div className={styles.processingStep}>
                    <i className="fas fa-spinner fa-spin" style={{color: '#ffc107'}}></i>
                    <span>{premiumProcessingStatus.currentProcess.current_step || 'Processing...'}</span>
                    {premiumProcessingStatus.currentProcess.progress_percentage && (
                      <div className={styles.progressBar}>
                        <div 
                          className={styles.progressFill} 
                          style={{width: `${premiumProcessingStatus.currentProcess.progress_percentage}%`}}
                        ></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className={styles.processingStep}>
                      <i className="fas fa-check-circle" style={{color: '#28a745'}}></i>
                      <span>Proposals analyzed</span>
                    </div>
                    <div className={styles.processingStep}>
                      <i className="fas fa-spinner fa-spin" style={{color: '#ffc107'}}></i>
                      <span>Generating clarification questions</span>
                    </div>
                    <div className={styles.processingStep}>
                      <i className="fas fa-clock" style={{color: '#6c757d'}}></i>
                      <span>Creating detailed reports</span>
                    </div>
                  </>
                )}
              </div>

              <div className={styles.processingInfo}>
                <div className={styles.infoItem}>
                  <i className="fas fa-clock"></i>
                  <span><strong>Estimated time:</strong> 3-5 minutes</span>
                </div>
                <div className={styles.infoItem}>
                  <i className="fas fa-envelope"></i>
                  <span><strong>Notification:</strong> You'll receive an email when complete</span>
                </div>
                <div className={styles.infoItem}>
                  <i className="fas fa-sync-alt"></i>
                  <span><strong>Auto-refresh:</strong> This page will update automatically</span>
                </div>
              </div>

              {premiumProcessingStatus && (
                <div className={styles.statusDebug}>
                  <p><small>
                    Active processes: {premiumProcessingStatus.activeProcesses} <br/>
                    Total processes: {premiumProcessingStatus.totalProcesses} <br/>
                    {premiumProcessingStatus.currentProcess && (
                      <>
                        Current step: {premiumProcessingStatus.currentProcess.current_step} <br/>
                        Progress: {premiumProcessingStatus.currentProcess.progress_percentage || 0}%
                      </>
                    )}
                  </small></p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.tabContent}>
        {error ? (
          <div className={styles.errorContainer}>
            <i className="fas fa-exclamation-circle"></i>
            <h3>Error</h3>
            <p>{error}</p>
            {/* <button onClick={() => {
              setError(null);
              navigate('/homeowner/dashboard');
            }} className={styles.backButton}>
              Back to Dashboard
            </button>
            <button onClick={() => {
              setError(null);
              window.location.reload();
            }} className={styles.retryButton}>
              <i className="fas fa-sync"></i> Retry
            </button> */}
          </div>
        ) : activeTab === 'overview' && (
          <ComparisonOverview 
            recommendationData={recommendationData}
            evaluationData={evaluationData}
            consolidatedData={consolidatedData}
            guestAnalysisData={guestAnalysisData}
            onUpgradeClick={handleUpgradeClick}
            userPlan={userPlan}
            hasActiveSubscription={isPremiumUnlocked}
          />
        )}
        
        {activeTab === 'detailed' && !isPremiumUnlocked && (
          <div className={styles.premiumBlocker}>
            <div className={styles.premiumBlockerContent}>
              <i className="fas fa-lock"></i>
              <h3>Essential Plan Required</h3>
              <p>Unlock detailed analysis with comprehensive contractor evaluation and risk assessment.</p>
              <button className={styles.unlockButton} onClick={handleUpgradeClick}>
                <i className="fas fa-crown"></i> Upgrade to Essential Plan
              </button>
            </div>
          </div>
        )}
        
        {activeTab === 'contractors' && (!isPremiumUnlocked || !['complete', 'expert'].includes(userPlan)) && (
          <div className={styles.premiumBlocker}>
            <div className={styles.premiumBlockerContent}>
              <i className="fas fa-lock"></i>
              <h3>Complete Plan Required</h3>
              <p>Get detailed contractor profiles, strengths, weaknesses, and recommendations.</p>
              <button className={styles.unlockButton} onClick={handleUpgradeClick}>
                <i className="fas fa-crown"></i> 
                {userPlan === 'free' 
                  ? 'Upgrade to Complete Plan' 
                  : userPlan === 'essential' 
                  ? 'Upgrade to Complete Plan'
                  : 'Upgrade to Complete Plan'
                }
              </button>
            </div>
          </div>
        )}
        
        {activeTab === 'questions' && (!isPremiumUnlocked || !['complete', 'expert'].includes(userPlan)) && (
          <div className={styles.premiumBlocker}>
            <div className={styles.premiumBlockerContent}>
              <i className="fas fa-lock"></i>
              <h3>Complete Plan Required</h3>
              <p>Access expert-curated questions to ask contractors before making your decision.</p>
              <button className={styles.unlockButton} onClick={handleUpgradeClick}>
                <i className="fas fa-crown"></i> 
                {userPlan === 'free' 
                  ? 'Upgrade to Complete Plan' 
                  : userPlan === 'essential' 
                  ? 'Upgrade to Complete Plan'
                  : 'Upgrade to Complete Plan'
                }
              </button>
            </div>
          </div>
        )}
        
        {activeTab === 'detailed' && isPremiumUnlocked && (
          <DetailedAnalysis 
            recommendationData={recommendationData}
            evaluationData={evaluationData}
            consolidatedData={consolidatedData}
            userPlan={userPlan}
            hasActiveSubscription={isPremiumUnlocked}
          />
        )}
        
        {activeTab === 'contractors' && isPremiumUnlocked && (
          <ContractorProfiles 
            consolidatedData={consolidatedData}
            evaluationData={evaluationData}
            userPlan={userPlan}
            hasActiveSubscription={isPremiumUnlocked}
            onUpgradeClick={handleUpgradeClick}
          />
        )}
        
        {activeTab === 'questions' && isPremiumUnlocked && (
          <ExpertQuestions 
            consolidatedData={consolidatedData}
            evaluationData={evaluationData}
            clarificationData={clarificationData}
            userPlan={userPlan}
            hasActiveSubscription={isPremiumUnlocked}
            onUpgradeClick={handleUpgradeClick}
            userId={savedComparisonData?.user_id}
            projectName={savedComparisonData?.project_name}
          />
        )}
      </div>
    </div>
  );
};

export default ProposalComparisonView; 